import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { TimelineItem, TimelineScale } from '../../../types/interfaces';
import { TimelineGroup } from '../types';

function offset(el: Element) {
  const rect = el.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return { top: rect.top + scrollTop };
}

export function useTimelineLayoutMetrics(args: {
  timelineScale: TimelineScale;
  groups: TimelineGroup[];
  items: TimelineItem[];
}) {
  const { timelineScale, groups, items } = args;

  const calendarAssignmentTableRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const employeesTimelineRef = useRef<HTMLDivElement>(null);

  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [scrollBarOffset, setScrollbarOffset] = useState(0);

  const tableElement = document.querySelector('.timeline-wrapper');
  const bottomGapHeight = 1;

  const [tableOffset, setTableOffset] = useState(() => {
    return tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
  });

  const updateTableOffset = useCallback(() => {
    const element = document.querySelector('.timeline-wrapper');
    if (element instanceof HTMLElement) {
      setTableOffset(offset(element).top);
    }
  }, []);

  useLayoutEffect(() => {
    updateTableOffset();
  }, [updateTableOffset, timelineScale]);

  useEffect(() => {
    const t = setTimeout(() => {
      updateTableOffset();
    }, 50);
    return () => clearTimeout(t);
  }, [updateTableOffset]);

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    return `calc(100vh - ${tableOffset + paginatorHeight + bottomGapHeight}px)`;
  }, [tableOffset, bottomGapHeight]);

  const checkScrollbar = useCallback(() => {
    const container = employeesTimelineRef.current;
    if (!container) {
      setScrollbarOffset(0);
      return;
    }

    const hasScrollbar = container.scrollHeight > container.clientHeight;

    if (hasScrollbar) {
      const scrollbarWidth = container.offsetWidth - container.clientWidth;
      setScrollbarOffset(scrollbarWidth || 15);
    } else {
      setScrollbarOffset(0);
    }
  }, []);

  const checkHorizontalScroll = useCallback(() => {
    const container = contentWrapperRef.current;
    if (!container) {
      setHasHorizontalScroll(false);
      return;
    }

    const hasHorizontal = container.scrollWidth > container.clientWidth;
    setHasHorizontalScroll(hasHorizontal);
  }, []);

  useEffect(() => {
    const handleResizeAndUpdate = () => {
      updateTableOffset();
      checkScrollbar();
      checkHorizontalScroll();
    };

    const timer = setTimeout(() => {
      updateTableOffset();
      checkScrollbar();
      checkHorizontalScroll();
    }, 50);

    window.addEventListener('resize', handleResizeAndUpdate);

    const container = contentWrapperRef.current;
    if (container) {
      container.addEventListener('scroll', checkHorizontalScroll);

      const observer = new MutationObserver(() => {
        checkHorizontalScroll();
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });

      return () => {
        window.removeEventListener('resize', handleResizeAndUpdate);
        container.removeEventListener('scroll', checkHorizontalScroll);
        observer.disconnect();
        clearTimeout(timer);
      };
    }

    return () => {
      window.removeEventListener('resize', handleResizeAndUpdate);
      clearTimeout(timer);
    };
  }, [checkScrollbar, checkHorizontalScroll, groups, items, updateTableOffset]);

  return {
    calendarAssignmentTableRef,
    contentWrapperRef,
    employeesTimelineRef,
    hasHorizontalScroll,
    scrollBarOffset,
    scrollHeight,
    checkScrollbar
  };
}
